import dayjs from 'dayjs';
import { AccessEvent, AccessEventsFilter, AccessEventsPage, MobileOpCode, Query } from 'graphql/generated/graphqlTypes';
import { generateMockAccessEvent } from 'mocks/graphqlMocks/accessEvent';
import { generateMockAccessEventDoor } from 'mocks/graphqlMocks/accessEventDoor';
import { generateMockAccessEventsPage } from 'mocks/graphqlMocks/accessEventPage';
import { generateMockAccessEventVisitor } from 'mocks/graphqlMocks/accessEventVisitor';
import { mockDoorReader1 } from 'mocks/graphqlMocks/doorFixtures';
import { mockVisitor1, mockVisitor2 } from 'mocks/graphqlMocks/visitorFixtures';

export function mockedGetAccessEventsForTenantData(
  getAccessEventsForTenant: AccessEventsPage
): Pick<Query, 'getAccessEventsForTenant'> {
  return {
    getAccessEventsForTenant
  };
}

export const mockedGetAccessEventsForTenantDataForQueryFirstFetch = mockedGetAccessEventsForTenantData(
  generateMockAccessEventsPage({
    items: [
      generateMockAccessEvent({
        id: 'mockedAccessEventsId',
        createdAt: dayjs().subtract(1, 'hour').toISOString(),
        visitor: generateMockAccessEventVisitor({
          id: mockVisitor1.id,
          name: mockVisitor1.name,
          email: mockVisitor1.email ?? undefined
        }),
        door: generateMockAccessEventDoor({
          id: mockDoorReader1.id,
          name: mockDoorReader1.name,
          type: mockDoorReader1.type
        })
      })
    ],
    nextToken: 'mockedNextToken'
  })
);

export const mockedGetAccessEventsForTenantDataForQuerySecondFetch = mockedGetAccessEventsForTenantData(
  generateMockAccessEventsPage({
    items: [
      generateMockAccessEvent({
        id: 'mockedAccessEventsId2',
        createdAt: dayjs().subtract(1, 'day').toISOString(),
        visitor: generateMockAccessEventVisitor({
          id: mockVisitor2.id,
          name: mockVisitor2.name,
          email: mockVisitor2.email ?? undefined
        }),
        door: generateMockAccessEventDoor({
          id: mockDoorReader1.id,
          name: mockDoorReader1.name,
          type: mockDoorReader1.type
        })
      })
    ]
  })
);

// A plausible office rhythm — quiet nights, a morning and an evening peak — indexed by hour of day,
// so the dashboard charts render a recognisable shape instead of a flat line in mocked mode.
const GRANTED_PER_HOUR_OF_DAY = [1, 0, 0, 0, 1, 3, 9, 21, 28, 18, 15, 19, 21, 17, 16, 16, 21, 24, 18, 10, 6, 3, 2, 1];
const DENIED_PER_HOUR_OF_DAY = [0, 0, 0, 0, 0, 1, 1, 2, 3, 2, 1, 2, 2, 1, 1, 1, 2, 3, 2, 1, 1, 0, 0, 0];

const EVENT_COUNTS_BY_ACTION: Partial<Record<MobileOpCode, number[]>> = {
  [MobileOpCode.AccessGranted]: GRANTED_PER_HOUR_OF_DAY,
  [MobileOpCode.AccessDenied]: DENIED_PER_HOUR_OF_DAY
};

const MOCK_VISITORS = [mockVisitor1, mockVisitor2];

const MINUTES_PER_HOUR = 60;

/**
 * The dashboard charts query a rolling time window, so their events have to be generated relative to
 * the window the caller asked for — a fixture with fixed timestamps falls out of the window and leaves
 * the charts empty. Honours the requested `actions` so each chart only gets the series it asked for.
 */
export const mockedGetAccessEventsForTenantDataForTimeWindow = (
  filter?: AccessEventsFilter | null
): Pick<Query, 'getAccessEventsForTenant'> => {
  const rangeStart = dayjs(filter?.dateRange?.rangeStart).startOf('hour');
  const rangeEnd = dayjs(filter?.dateRange?.rangeEnd);
  const requestedActions = filter?.actions?.length
    ? (filter.actions.filter(Boolean) as MobileOpCode[])
    : [MobileOpCode.AccessGranted, MobileOpCode.AccessDenied];

  const hoursInRange = Math.max(rangeEnd.diff(rangeStart, 'hour') + 1, 0);

  const items = Array.from({ length: hoursInRange }).flatMap<AccessEvent>((_, hourOffset) => {
    const hour = rangeStart.add(hourOffset, 'hour');

    return requestedActions.flatMap((action) => {
      const count = EVENT_COUNTS_BY_ACTION[action]?.[hour.hour()] ?? 0;

      return Array.from({ length: count }, (_unused, index) => {
        const visitor = MOCK_VISITORS[index % MOCK_VISITORS.length];

        return generateMockAccessEvent({
          id: `mockedTimeWindowAccessEvent-${hour.format()}-${action}-${index}`,
          // spread evenly across the whole hour rather than one per minute, so charts that bucket
          // by sub-hour slots get a smooth shape instead of everything piling into the first slots
          createdAt: hour.add(Math.floor((index * MINUTES_PER_HOUR) / count), 'minute').toISOString(),
          action,
          visitor: generateMockAccessEventVisitor({
            id: visitor.id,
            name: visitor.name,
            email: visitor.email ?? undefined
          }),
          door: generateMockAccessEventDoor({
            id: mockDoorReader1.id,
            name: mockDoorReader1.name,
            type: mockDoorReader1.type
          })
        });
      });
    });
  });

  return mockedGetAccessEventsForTenantData(generateMockAccessEventsPage({ items }));
};

export const mockedGetAccessEventsForTenantDataForQueryWithFiltersFirstFetch = mockedGetAccessEventsForTenantData(
  generateMockAccessEventsPage({
    items: [
      generateMockAccessEvent({
        id: 'mockedAccessEventsIdWithFilters',
        createdAt: dayjs().subtract(2, 'hour').toISOString(),
        visitor: generateMockAccessEventVisitor({
          id: mockVisitor1.id,
          name: mockVisitor1.name,
          email: mockVisitor1.email ?? undefined
        }),
        door: generateMockAccessEventDoor({
          id: mockDoorReader1.id,
          name: mockDoorReader1.name,
          type: mockDoorReader1.type
        })
      })
    ],
    nextToken: 'mockedNextTokenWithFilters'
  })
);

export const mockedGetAccessEventsForTenantDataForQueryWithFiltersSecondFetch = mockedGetAccessEventsForTenantData(
  generateMockAccessEventsPage({
    items: [
      generateMockAccessEvent({
        id: 'mockedAccessEventsIdWithFilters2',
        createdAt: dayjs().subtract(2, 'day').toISOString(),
        visitor: generateMockAccessEventVisitor({
          id: mockVisitor1.id,
          name: mockVisitor1.name,
          email: mockVisitor1.email ?? undefined
        }),
        door: generateMockAccessEventDoor({
          id: mockDoorReader1.id,
          name: mockDoorReader1.name,
          type: mockDoorReader1.type
        })
      })
    ]
  })
);
